// Default API base URL
const DEFAULT_API_BASE_URL = 'https://play.hey.gg';

/**
 * Gets the variant assigned to the current user for a specific experiment
 * @param experiment The experiment identifier
 * @param defaultVariant The default variant to return if the experiment is inactive or no variant is assigned
 * @returns A promise that resolves to the variant object with id, name, and config properties
 * @throws Error if the experiment is not found (404) or other API errors occur
 */
async function getVariant(
  experiment: string,
  defaultVariant: string = 'default',
): Promise<{ id?: number; name: string; config?: any[] }> {
  console.log(`[AB Testing] Getting variant for experiment: ${experiment}`);

  // Check for query parameter override (for development/testing)
  const urlParams = new URLSearchParams(window.location.search);
  const overrideVariant = urlParams.get(`ab[${experiment}]`);
  
  if (overrideVariant) {
    console.log(`[AB Testing] Using override variant from URL: ${overrideVariant} for experiment: ${experiment}`);
    return {
      name: overrideVariant,
    };
  }

  // Check if variant is already cached in local storage
  const abTestsJson = localStorage.getItem('heygg_ab_tests');
  const abTests = abTestsJson ? JSON.parse(abTestsJson) : {};

  if (abTests[experiment]) {
    console.log('[AB Testing] Using cached variant:', abTests[experiment]);
    return abTests[experiment];
  }

  const apiBaseUrl = window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
  const accessToken = localStorage.getItem('heygg_jwt');
  const anonymousId = localStorage.getItem('heygg_anonymous_id');
  console.log(`[AB Testing] Using API base URL: ${apiBaseUrl}`);

  const headers: { 'Content-Type': string; Authorization?: string } = {
    'Content-Type': 'application/json',
  };

  let url = `${apiBaseUrl}/api/ab-testing/${experiment}/variant`;

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  } else if (anonymousId) {
    url += `?anonymous_id=${anonymousId}`;
  } else {
    throw new Error('User is not authenticated or anonymous');
  }

  try {
    console.log(`[AB Testing] Fetching variant from: ${url}`);
    console.log(`[AB Testing] Headers:`, headers);

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    // If the experiment is not found (404), throw an error
    if (response.status === 404) {
      console.error(`[AB Testing] Experiment not found: ${experiment}`);
      throw new Error(`Experiment not found: ${experiment}`);
    }

    if (!response.ok) {
      console.error(`[AB Testing] Failed to get variant: ${response.status}`);
      throw new Error(`Failed to get variant: ${response.status}`);
    }

    const data = await response.json();
    console.log('[AB Testing] Received response:', data);

    // If experiment is not active, return the default variant
    if (data.experiment?.status !== 'active') {
      console.warn(
        `[AB Testing] Experiment is not active (status: ${data.experiment?.status}): ${experiment}, returning default variant: ${defaultVariant}`,
      );
      return {
        name: defaultVariant,
      };
    }

    // If no variant is returned but experiment exists, return default variant
    if (!data.variant) {
      console.warn(
        `[AB Testing] No variant returned for experiment: ${experiment}, using default: ${defaultVariant}`,
      );
      return {
        name: defaultVariant,
      };
    }

    // Cache the variant in localStorage
    const abTestsJson = localStorage.getItem('heygg_ab_tests');
    const abTests = abTestsJson ? JSON.parse(abTestsJson) : {};
    abTests[experiment] = data.variant;
    localStorage.setItem('heygg_ab_tests', JSON.stringify(abTests));
    console.log(
      `[AB Testing] Cached variant ${data.variant.name} for experiment ${experiment}`,
    );

    return data.variant;
  } catch (err) {
    console.error('Error fetching AB test variant:', err);
    throw err;
  }
}

/**
 * Tracks an event for the current user in a specific experiment
 * @param experiment The experiment identifier
 * @param eventName The name of the event to track
 * @param eventData Additional data to associate with the event
 * @returns A promise that resolves when the event is tracked
 */
async function trackEvent(
  experiment: string,
  eventName: string,
  eventData: Record<string, any> = {},
): Promise<void> {
  console.log(
    `[AB Testing] Tracking event: ${eventName} for experiment: ${experiment}`,
  );
  console.log(`[AB Testing] Event data:`, eventData);

  const apiBaseUrl = window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
  const accessToken = localStorage.getItem('heygg_jwt');
  const anonymousId = localStorage.getItem('heygg_anonymous_id');
  console.log(`[AB Testing] Using API base URL: ${apiBaseUrl}`);

  const headers: { 'Content-Type': string; Authorization?: string } = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const body: {
    event: string;
    data: Record<string, any>;
    anonymous_id?: string;
  } = {
    event: eventName,
    data: eventData,
  };

  if (!accessToken && anonymousId) {
    body.anonymous_id = anonymousId;
  }

  try {
    const url = `${apiBaseUrl}/api/ab-testing/${experiment}/event`;
    console.log(`[AB Testing] Sending event to: ${url}`);
    console.log(`[AB Testing] Request body:`, body);
    console.log(`[AB Testing] Headers:`, headers);

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error(`[AB Testing] Failed to track event: ${response.status}`);
      throw new Error(`Failed to track event: ${response.status}`);
    }

    console.log(`[AB Testing] Event tracked successfully`);
  } catch (err) {
    console.error('[AB Testing] Error tracking AB test event:', err);
    throw err;
  }
}

/**
 * Gets all active experiments for the current site/game
 * @param gameKey Optional game key to filter experiments by
 * @returns A promise that resolves to an array of experiment objects
 */
async function getAllExperiments(gameKey?: string): Promise<Array<{
  id: number;
  game_key: string | null;
  name: string;
  status: string;
  variants: Array<{ id: number; name: string; key: string | null; distribution_weight: number; config: any[] }>;
}>> {
  console.log(`[AB Testing] Getting all experiments${gameKey ? ` for game: ${gameKey}` : ''}`);

  const apiBaseUrl = window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
  const accessToken = localStorage.getItem('heygg_jwt');
  const anonymousId = localStorage.getItem('heygg_anonymous_id');

  const headers: { 'Content-Type': string; Authorization?: string } = {
    'Content-Type': 'application/json',
  };

  let url = `${apiBaseUrl}/api/ab-testing/experiments`;
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  if (gameKey) {
    queryParams.append('game_key', gameKey);
  }
  
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  } else if (anonymousId) {
    queryParams.append('anonymous_id', anonymousId);
  } else {
    throw new Error('User is not authenticated or anonymous');
  }
  
  // Add query parameters to URL if any exist
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  try {
    console.log(`[AB Testing] Fetching experiments from: ${url}`);
    console.log(`[AB Testing] Headers:`, headers);

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      console.error(`[AB Testing] Failed to get experiments: ${response.status}`);
      throw new Error(`Failed to get experiments: ${response.status}`);
    }

    const responseData = await response.json();
    console.log('[AB Testing] Received response:', responseData);

    // Extract experiments from the data wrapper
    const experiments = responseData.data || [];
    console.log(`[AB Testing] Found ${experiments.length} experiments`);

    return experiments;
  } catch (err) {
    console.error('Error fetching experiments:', err);
    throw err;
  }
}

const ABTesting = {
  getVariant,
  trackEvent,
  getAllExperiments,
};

export default ABTesting;
